import React, { useState, useEffect } from 'react';
import { systemMonitoringService } from '../../services/systemMonitoringService';
import { useLanguage } from '../../contexts/LanguageContext';
import HealthStatusOverview from './HealthStatusOverview';
import LiveMetricsMonitor from './LiveMetricsMonitor';
import DatabaseHealthPanel from './DatabaseHealthPanel';
import IncidentTrackingSystem from './IncidentTrackingSystem';
import PerformanceAnalytics from './PerformanceAnalytics';
import './SystemHealthDashboard.scss';

const SystemHealthDashboard = () => {
    const [healthData, setHealthData] = useState(null);
    const [metricsData, setMetricsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const { t } = useLanguage();

    const loadSystemData = async () => {
        try {
            setLoading(true);
            const [healthResponse, metricsResponse] = await Promise.all([
                systemMonitoringService.fetchSystemHealthStatus(),
                systemMonitoringService.fetchAdvancedSystemMetrics()
            ]);

            if (healthResponse.success) {
                setHealthData(healthResponse.data);
            }
            if (metricsResponse.success) {
                setMetricsData(metricsResponse.data);
            }
            setLastRefreshed(new Date());
        } catch (error) {
            console.error('System health data yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSystemData();

        // Oto refresh
        if (autoRefresh) {
            const interval = setInterval(loadSystemData, 30000); // 30 saniyede bir
            return () => clearInterval(interval);
        }
    }, [autoRefresh]);

    const handleRefresh = async () => {
        await loadSystemData();
    };

    const handleAutoRefreshToggle = () => {
        setAutoRefresh(!autoRefresh);
    };

    if (loading && !healthData) {
        return (
            <div className="system-health-loading">
                <div className="loading-spinner"></div>
                <div className="loading-text">{t('loading')}</div>
            </div>
        );
    }

    return (
        <div className="system-health-dashboard">
            {/* Header */}
            <div className="dashboard-header">
                <div className="header-content">
                    <h1>{t('systemHealthDashboard')}</h1>
                    <p>{t('systemHealthDashboardDesc')}</p>
                </div>
                <div className="header-controls">
                    <div className="refresh-info">
                        <span className="last-refresh">
                            {t('systemHealthLastRefreshTime')}: {lastRefreshed?.toLocaleTimeString()}
                        </span>
                        <label className="auto-refresh-toggle">
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={handleAutoRefreshToggle}
                            />
                            <span className="toggle-slider"></span>
                            {t('auto_refresh_systemHealth')} (30s)
                        </label>
                    </div>
                    <button className="refresh-button" onClick={handleRefresh}>
                        {t('systemHealthRefreshData')}
                    </button>
                </div>
            </div>

            {/* Ana İçerik */}
            <div className="dashboard-content">
                {/* Sistem Genel Durumu */}
                <div className="health-overview-section">
                    <HealthStatusOverview
                        healthData={healthData}
                        metricsData={metricsData}
                    />
                </div>

                {/* Canlı Metrikler */}
                <div className="metrics-section">
                    <LiveMetricsMonitor metricsData={metricsData} />
                </div>

                {/* Database ve Performans */}
                <div className="analytics-section">
                    <div className="analytics-column">
                        <DatabaseHealthPanel data={healthData?.database} />
                    </div>
                    <div className="analytics-column">
                        <PerformanceAnalytics
                            healthData={healthData}
                            metricsData={metricsData}
                        />
                    </div>
                </div>

                {/* Olay Takip Sistemi */}
                <div className="incidents-section">
                    <IncidentTrackingSystem
                        errors={healthData?.errors}
                        activeUsers={healthData?.activeUsers}
                    />
                </div>
            </div>
        </div>
    );
};

export default SystemHealthDashboard;